import {useEffect, useState, useRef, useCallback} from 'react'

/** debounces a value that you are changing outside this hook to send to onChange */
export function useOnDebounce<T = any>(
  value: T,
  {enabled = true, debounce = 1000, onChange}: {enabled?: boolean; debounce?: number; onChange?: (value: T) => void}
) {
  const [debouncedValue, setDebouncedValue] = useState(value)
  const mountedRef = useRef(false)
  const timeoutRef = useRef<number | null>(null)

  const cancel = () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
  }

  useEffect(() => {
    if (!mountedRef.current || !enabled) return
    cancel()
    timeoutRef.current = window.setTimeout(() => {
      setDebouncedValue(value)
      onChange?.(value)
    }, debounce)
  }, [value, debounce])

  useEffect(() => {
    mountedRef.current = true
    return cancel
  }, [])

  return debouncedValue
}

/** forked from https://vscode.dev/github/mantinedev/mantine/blob/master/packages/%40mantine/hooks/src/use-debounced-value/use-debounced-value.ts  */

export function useDebouncedValue<T = any>(
  value: T,
  wait: number,
  options?: {leading?: boolean; isImmediateChange?: (newValue: T, oldValue: T) => boolean}
) {
  const [_value, setValue] = useState(value)
  const timeoutRef = useRef<number | null>(null)
  const cooldownRef = useRef(false)
  const cancel = () => window.clearTimeout(timeoutRef.current!)

  useEffect(() => {
    if (!cooldownRef.current && options?.leading) {
      cooldownRef.current = true
      setValue(value)
    } else {
      cancel()
      if (options?.isImmediateChange?.(value, _value)) {
        cooldownRef.current = false
        setValue(value)
        return
      } else
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false
          setValue(value)
        }, wait)
    }
  }, [value, options?.leading, wait])

  return [_value, cancel] as const
}

/** debounce calls to fn. If no call has been made for *delay* then it will be called immediately  */
export const useThrottledFn = <T extends (...args: any[]) => any>(callback: T, delay: number): T => {
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const lastCallTimeRef = useRef<number | null>(null)

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now()

      const shouldCallImmediately = lastCallTimeRef.current === null || now - lastCallTimeRef.current > delay

      if (shouldCallImmediately) {
        callback(...args)
        lastCallTimeRef.current = now
      } else {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
        }

        timerRef.current = setTimeout(() => {
          callback(...args)
          lastCallTimeRef.current = Date.now()
        }, delay) as NodeJS.Timeout
      }
    },
    [callback, delay]
  )

  return debouncedCallback as T
}

export const useDebouncedFn = <T extends (...args: any[]) => any>(callback: T, delay: number): T => {
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timerRef.current) clearTimeout(timerRef.current)

      timerRef.current = setTimeout(() => {
        callback(...args)
      }, delay) as NodeJS.Timeout
    },
    [callback, delay]
  )

  return debouncedCallback as T
}
